import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FavoriteListDto } from '@/services/platform/api/model';
import { useClientTranslation } from '@/lib/core/i18n';
import { useLocaleStore } from '@/lib/core/stores';
import {
  useAddFavoriteItem,
  useFindFavoriteList,
  useRemoveFavoriteItem,
} from '@/services/platform/data/shop';
import { getMeFavoriteItemsV1 } from '@/services/platform/api/me-—-favorites/me-—-favorites';
import { CreateNewListSheet } from '@/components/shared/profile/favorites/sheets/CreateNewListSheet';
import { FavoritesActionToast } from '@/components/shared/profile/favorites/ui/FavoritesActionToast';
import { AddToWishlistSheet } from '@/components/shared/shop/wishlist/sheets/AddToWishlistSheet';
import { HeartToggle } from '@/components/shared/shop/wishlist/ui/HeartToggle';

/** `itemType` used for events in the shared shop favourites API (a free string, not shop-restricted). */
const EVENT_ITEM_TYPE = 'event';

export interface EventFavoriteToggleHandle {
  /** Opens the "add to wishlist" sheet — for triggers other than the heart icon itself. */
  open: () => void;
}

export interface EventFavoriteToggleProps {
  event: {
    id: string | undefined;
    title?: string | null;
    imageUrl?: string | null;
  };
  /** The user's 'Events' favorite lists — fetch once at the parent screen and share across cards. */
  favoriteLists: FavoriteListDto[];
  refetchFavoriteLists: () => Promise<void>;
  size?: number;
  className?: string;
  disabled?: boolean;
  /** Notified whenever the favorited state changes, for parents that also reflect it elsewhere. */
  onFavoritedChange?: (isFavorited: boolean) => void;
  /**
   * Batch mode — for lists rendering many events, where the parent already knows which
   * list (if any) contains each event (via `useEventFavoriteIndex`) and refetches that
   * index as a whole. When `onRefetchNeeded` is provided, this component skips its own
   * per-event `/favorites/lookup` call and trusts `favoriteListGuid` instead. Omit both
   * for a single-event screen (e.g. event detail) to keep the simpler, self-contained lookup.
   */
  favoriteListGuid?: string;
  onRefetchNeeded?: () => Promise<void>;
}

/**
 * Self-contained "favorite" control for an event: the heart icon plus the
 * add-to-wishlist / create-list sheets and confirmation toast — mirrors
 * `ProductFavoriteToggle` (Shop) so the add/remove-favorite UX is identical
 * across Shop products and Events (search, calendar, detail).
 */
export const EventFavoriteToggle = forwardRef<EventFavoriteToggleHandle, EventFavoriteToggleProps>(
  function EventFavoriteToggle(
    {
      event,
      favoriteLists,
      refetchFavoriteLists,
      size = 24,
      className,
      disabled,
      onFavoritedChange,
      favoriteListGuid,
      onRefetchNeeded,
    },
    ref
  ) {
    const t = useClientTranslation();
    const navigate = useNavigate();
    const locale = useLocaleStore(state => state.locale);
    const isBatchMode = onRefetchNeeded != null;
    const { data: ownFavoriteList, refetch: refetchOwnFavoriteList } = useFindFavoriteList(
      EVENT_ITEM_TYPE,
      isBatchMode ? undefined : event.id
    );
    const { addItem: addFavoriteItem, createList } = useAddFavoriteItem();
    const { removeItem: removeFavoriteItem } = useRemoveFavoriteItem();

    const existingFavoriteListGuid = isBatchMode ? favoriteListGuid : ownFavoriteList?.publicGuid;
    const refetchExistingFavoriteList = isBatchMode ? onRefetchNeeded! : refetchOwnFavoriteList;
    const isFavorited = existingFavoriteListGuid != null;
    const [isAddToWishlistOpen, setAddToWishlistOpen] = useState(false);
    const [isCreateListOpen, setCreateListOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [toastListGuid, setToastListGuid] = useState<string | null>(null);
    const [toastKey, setToastKey] = useState(0);

    useEffect(() => {
      onFavoritedChange?.(isFavorited);
    }, [isFavorited, onFavoritedChange]);

    useImperativeHandle(ref, () => ({ open: () => setAddToWishlistOpen(true) }), []);

    async function addEventToList(list: FavoriteListDto) {
      if (!event.id || !list.publicGuid) return;
      await addFavoriteItem(list.publicGuid, {
        itemType: EVENT_ITEM_TYPE,
        itemExternalId: event.id,
        itemName: event.title ?? null,
        itemImageUrl: event.imageUrl ?? null,
      });
      setAddToWishlistOpen(false);
      setToastMessage(t('App.Shop.Wishlist.AddedToList', { listName: list.name ?? '' }));
      setToastListGuid(list.publicGuid);
      setToastKey(k => k + 1);
      await refetchExistingFavoriteList();
    }

    async function handleSelectFavoriteList(listGuid: string) {
      const list = favoriteLists.find(l => l.publicGuid === listGuid);
      if (!list) return;
      await addEventToList(list);
    }

    async function handleDeselectFavoriteList(listGuid: string) {
      if (!event.id) return;
      const list = favoriteLists.find(l => l.publicGuid === listGuid);
      const response = await getMeFavoriteItemsV1(listGuid);
      if (response.status !== 200) return;
      const item = response.data.find(i => i.itemType === EVENT_ITEM_TYPE && i.itemExternalId === event.id);
      if (!item?.publicGuid) return;
      const removed = await removeFavoriteItem(listGuid, item.publicGuid);
      if (!removed) return;
      setAddToWishlistOpen(false);
      setToastMessage(t('App.Shop.Wishlist.RemovedFromList', { listName: list?.name ?? '' }));
      setToastListGuid(null);
      setToastKey(k => k + 1);
      await refetchExistingFavoriteList();
    }

    async function handleCreateList(name: string) {
      const created = await createList(name, 'Events');
      if (!created) return;
      await refetchFavoriteLists();
      setCreateListOpen(false);
      await addEventToList(created);
    }

    return (
      <>
        <HeartToggle
          isFavorited={isFavorited}
          onToggle={() => setAddToWishlistOpen(true)}
          size={size}
          className={className}
          disabled={disabled}
        />

        {/* See ProductFavoriteToggle: stops the sheet's clicks (Radix portal, React-tree bubbling)
            from reaching a clickable card underneath and navigating away before the toast shows. */}
        <div onClick={e => e.stopPropagation()}>
          <AddToWishlistSheet
            open={isAddToWishlistOpen}
            onOpenChange={setAddToWishlistOpen}
            product={{
              image: event.imageUrl ?? undefined,
              name: event.title ?? undefined,
            }}
            lists={favoriteLists}
            selectedListGuid={existingFavoriteListGuid}
            onSelectList={handleSelectFavoriteList}
            onDeselectList={handleDeselectFavoriteList}
            onCreateNewList={() => setCreateListOpen(true)}
          />

          <CreateNewListSheet
            open={isCreateListOpen}
            onOpenChange={setCreateListOpen}
            listType="Events"
            onCreate={handleCreateList}
          />

          <FavoritesActionToast
            key={toastKey}
            open={toastMessage !== null}
            anchor="bottom"
            message={toastMessage ?? ''}
            actionLabel={toastListGuid ? t('App.Shop.Wishlist.GoToList') : undefined}
            onAction={
              toastListGuid
                ? () => {
                    const localeSegment = locale?.split('-')[0] ?? 'en';
                    navigate(`/${localeSegment}/profile/favorites?list=${toastListGuid}`);
                  }
                : undefined
            }
            onDismiss={() => setToastMessage(null)}
          />
        </div>
      </>
    );
  }
);
